import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Instagram, Facebook, Mail, MapPin, Shield, Truck, Phone } from 'lucide-react';

const PETSBOX_LOGO = "https://customer-assets.emergentagent.com/job_paws-box/artifacts/gyymiifm_f89199bc-32c1-4646-a02f-5de9041d5b0a.png";

const PARTNER_LOGOS = "/assets/partner_logos.png";

const ANPC_IMAGE = "https://customer-assets.emergentagent.com/job_515449cf-5538-46e3-93ca-14563ce96337/artifacts/ghadbhuz_anpc.webp";

function LogoBadges({ className = "" }) {
  return (
    <div className={`flex items-center ${className}`}>
      <img src={PARTNER_LOGOS} alt="DPD Romania, Visa, Mastercard" className="h-10 object-contain" />
    </div>
  );
}


export default function Footer({ onSubscribeClick }) {
  const { t, isRomanian, getBasePrice } = useLanguage();
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToSection = (e, sectionId) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-brand-cream-dark border-t border-brand-border">
      {/* CTA Section */}
      <div className="bg-brand-brown py-12">
        <div className="section-container text-center">
          <h3 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
            {isRomanian ? 'Gata sa incepi?' : 'Ready to start?'}
          </h3>
          <p className="text-white/80 mb-6 max-w-xl mx-auto">
            {isRomanian 
              ? 'Aboneaza-te acum si primeste prima cutie personalizata pentru prietenul tau blanos!'
              : 'Subscribe now and receive your first personalized box for your furry friend!'}
          </p>
          <button
            onClick={onSubscribeClick}
            className="bg-brand-orange text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-brand-orange-hover transition-colors shadow-xl"
            data-testid="footer-cta"
          >
            {t('hero_cta')} - {isRomanian ? `${getBasePrice()} lei/luna` : `\u20AC${getBasePrice()}/month`}
          </button>
          
          {/* Trust badges */}
          <div className="flex flex-wrap items-center justify-center gap-4 mt-6">
            <div className="flex items-center gap-2 text-white/80 text-sm">
              <Shield className="w-4 h-4" />
              {t('trust_secure')}
            </div>
            <div className="flex items-center gap-2 text-white/80 text-sm">
              <Truck className="w-4 h-4" />
              {t('trust_shipping')}
            </div>
            <div className="flex items-center gap-2 text-white/80 text-sm">
              {'\u{1F1F7}\u{1F1F4}'} {'\u{1F1E7}\u{1F1EC}'} {t('trust_warehouse')}
            </div>
          </div>

          {/* Courier & Payment Logos */}
          <div className="mt-4">
            <LogoBadges className="justify-center" />
          </div>
        </div>
      </div>

      <div className="section-container section-padding">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <img src={PETSBOX_LOGO} alt="PetsBox" className="w-14 h-14 rounded-full" />
            </div>
            <p className="text-brand-muted text-sm leading-relaxed mb-4">
              {t('footer_tagline')}
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.instagram.com/petsbox_eu"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white border border-brand-border flex items-center justify-center hover:border-brand-orange hover:text-brand-orange transition-colors"
                data-testid="footer-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://www.facebook.com/petsboxeu"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white border border-brand-border flex items-center justify-center hover:border-brand-orange hover:text-brand-orange transition-colors"
                data-testid="footer-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold text-brand-brown mb-4">{t('footer_quick_links')}</h4>
            <ul className="space-y-3">
              <li>
                <a href="#how-it-works" onClick={(e) => scrollToSection(e, 'how-it-works')} className="text-brand-muted hover:text-brand-orange transition-colors cursor-pointer">
                  {t('nav_how_it_works')}
                </a>
              </li>
              <li>
                <a href="#pricing" onClick={(e) => scrollToSection(e, 'pricing')} className="text-brand-muted hover:text-brand-orange transition-colors cursor-pointer">
                  {isRomanian ? 'Pret' : 'Pricing'}
                </a>
              </li>
              <li>
                <a href="#whats-inside" onClick={(e) => scrollToSection(e, 'whats-inside')} className="text-brand-muted hover:text-brand-orange transition-colors cursor-pointer">
                  {t('nav_whats_inside')}
                </a>
              </li>
              <li>
                <a href="#faq" onClick={(e) => scrollToSection(e, 'faq')} className="text-brand-muted hover:text-brand-orange transition-colors cursor-pointer">
                  {t('nav_faq')}
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-heading font-semibold text-brand-brown mb-4">{t('footer_legal')}</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/terms" className="text-brand-muted hover:text-brand-orange transition-colors">
                  {t('footer_terms')}
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-brand-muted hover:text-brand-orange transition-colors">
                  {t('footer_privacy')}
                </Link>
              </li>
              <li>
                <Link to="/shipping" className="text-brand-muted hover:text-brand-orange transition-colors">
                  {t('footer_shipping')}
                </Link>
              </li>
              <li>
                <Link to="/returns" className="text-brand-muted hover:text-brand-orange transition-colors">
                  {t('footer_returns')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading font-semibold text-brand-brown mb-4">{t('footer_contact')}</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-brand-muted">
                <Mail className="w-5 h-5 text-brand-orange" />
                <a href="mailto:hello@petsbox.eu" className="hover:text-brand-orange transition-colors">
                  hello@petsbox.eu
                </a>
              </li>
              <li className="flex items-center gap-3 text-brand-muted">
                <Phone className="w-5 h-5 text-brand-orange" />
                <a href="tel:+359878433982" className="hover:text-brand-orange transition-colors">
                  +359 878 433 982
                </a>
              </li>
              <li className="flex items-center gap-3 text-brand-muted">
                {/* Viber */}
                <svg className="w-5 h-5 text-[#7360F2]" viewBox="0 0 24 24" fill="currentColor"><path d="M11.4 0C9.47.03 5.67.36 3.55 2.28 1.94 3.81 1.34 6.04 1.27 8.8c-.07 2.76-.16 7.94 4.87 9.32v2.13s-.04.86.53 1.04c.69.22 1.09-.44 1.75-1.15.36-.39.86-.96 1.24-1.39 3.41.29 6.03-.37 6.33-.46.7-.22 4.67-.73 5.31-5.98.66-5.41-.32-8.83-2.1-10.37C17.47.41 14.4.02 12.57 0h-1.17zm.69 1.78h.96c1.55.02 4.24.3 5.6 1.53 1.48 1.23 2.3 4.24 1.74 8.8-.5 4.16-3.47 4.56-4.05 4.74-.25.08-2.54.64-5.41.44 0 0-2.14 2.58-2.81 3.27-.1.11-.23.16-.31.14-.12-.03-.15-.17-.15-.38l.02-3.56c-4.18-1.15-3.93-5.37-3.87-7.77.06-2.4.55-4.24 1.86-5.5C7 2.11 9.55 1.81 11.09 1.78h1zm-.3 3.07c-.19 0-.19.28 0 .29 1.38.04 2.56.52 3.4 1.37.84.84 1.27 1.97 1.34 3.34 0 .2.3.19.3 0-.08-1.48-.55-2.72-1.47-3.64-.91-.92-2.17-1.42-3.56-1.46zm-2.37.88c-.26-.04-.53.06-.7.29l-.56.68c-.19.25-.2.58-.05.85.42.76 1 1.54 1.73 2.28.86.87 1.72 1.49 2.6 2.01.27.16.61.14.85-.06l.62-.54c.28-.24.37-.63.2-.95-.36-.68-.92-1.25-.92-1.25-.19-.2-.47-.23-.7-.08l-.56.37s-.48.33-1.33-.53c-.85-.85-.53-1.33-.53-1.33l.37-.56c.15-.23.12-.52-.08-.7 0 0-.57-.57-1.25-.92a.77.77 0 0 0-.33-.06h.14zm4.13.94c-.19 0-.19.28 0 .29.86.04 1.59.34 2.13.88.53.54.81 1.24.85 2.1 0 .2.3.19.29 0-.04-.97-.37-1.77-.97-2.37-.6-.6-1.38-.92-2.3-.96zm-.08 1.48c-.18 0-.18.28 0 .29.55.04 1 .28 1.3.67.24.32.36.73.38 1.17 0 .2.29.19.29 0-.02-.51-.16-.99-.44-1.37-.37-.49-.91-.76-1.53-.8z"/></svg>
                <a href="viber://chat?number=+359878433982" className="hover:text-brand-orange transition-colors">Viber</a>
              </li>
              <li className="flex items-center gap-3 text-brand-muted">
                {/* WhatsApp */}
                <svg className="w-5 h-5 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                <a href="https://wa.me/359878433982" target="_blank" rel="noopener noreferrer" className="hover:text-brand-orange transition-colors">WhatsApp</a>
              </li>
              <li className="flex items-center gap-3 text-brand-muted">
                <Instagram className="w-5 h-5 text-brand-orange" />
                <a href="https://www.instagram.com/petsbox_eu" target="_blank" rel="noopener noreferrer" className="hover:text-brand-orange transition-colors">
                  @petsbox
                </a>
              </li>
              <li className="flex items-start gap-3 text-brand-muted">
                <MapPin className="w-5 h-5 text-brand-orange mt-0.5" />
                <span>Sofia district, Opitsvet, str. 23<br/>{'\u{1F1E7}\u{1F1EC}'} Bulgaria</span>
              </li>
            </ul>
          </div>
        </div>

        {/* ANPC Consumer Protection Badge */}
        <div className="mt-8 pt-8 border-t border-brand-border">
          <div className="flex items-center gap-3">
            <a 
              href="https://anpc.ro/ce-este-sal/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block hover:opacity-80 transition-opacity"
              data-testid="anpc-badge"
            >
              <img 
                src={ANPC_IMAGE} 
                alt="ANPC - Solutionarea Alternativa a Litigiilor" 
                className="h-14 md:h-16 object-contain rounded-lg"
              />
            </a>
            <p className="text-xs text-brand-muted max-w-sm">
              {isRomanian 
                ? 'Autoritatea Nationala pentru Protectia Consumatorilor - Solutionarea Alternativa a Litigiilor'
                : 'National Authority for Consumer Protection - Alternative Dispute Resolution'}
            </p>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-8 pt-8 border-t border-brand-border">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-brand-muted text-sm">
              &copy; {currentYear} KNS TRADING EUROPE (PetsBox). {t('footer_rights')} | VAT: BG207617173
            </p>
            <div className="flex items-center gap-4">
              <p className="text-brand-muted text-xs">
                {t('footer_country')}
              </p>
              <LogoBadges />
            </div>
          </div>
        </div>
      </div>

      {/* Floating WhatsApp Live Chat Button */}
      <a
        href="https://wa.me/359878433982"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white pl-4 pr-5 py-3 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105"
        data-testid="whatsapp-float-btn"
      >
        <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" xmlns="http://www.w3.org/2000/svg">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        <div className="flex flex-col leading-tight">
          <span className="text-sm font-bold">ONLINE</span>
          <span className="text-[11px] opacity-90">{isRomanian ? 'Hai să vorbim!' : "Let's chat!"}</span>
        </div>
        <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-300 rounded-full animate-ping"></span>
        <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full"></span>
      </a>
    </footer>
  );
}
